/**
 * Documentation Service
 * Manages documentation entries, quick help text, and navigation to detailed docs
 */

class DocumentationService {
  constructor() {
    this.documentationMap = new Map();
    this.initialized = false;
  }

  /**
   * Initialize the documentation service with all documentation entries
   */
  async initialize() {
    if (this.initialized) return;
    
    try {
      // Import all documentation entries
      const { documentationEntries } = await import('../documentation/entries');
      
      // Build the documentation map for quick access
      documentationEntries.forEach(entry => {
        this.documentationMap.set(entry.id, entry);
      });
      
      this.initialized = true;
      console.log(`Documentation service initialized with ${this.documentationMap.size} entries`);
    } catch (error) {
      console.error('Failed to initialize documentation service:', error);
    }
  }

  /**
   * Get documentation entry by ID
   * @param {string} id - The documentation entry ID
   * @returns {Object|null} The documentation entry or null if not found
   */
  getEntry(id) {
    if (!this.initialized) {
      console.warn('Documentation service not initialized. Call initialize() first.');
      return null;
    }
    
    return this.documentationMap.get(id) || null;
  }

  /**
   * Get quick help text for a component
   * @param {string} id - The documentation entry ID
   * @returns {string} Quick help text (2 lines max)
   */
  getQuickHelp(id) {
    const entry = this.getEntry(id);
    return entry?.quickHelp || 'No documentation available for this element.';
  }

  /**
   * Get the full documentation URL/path for a component
   * @param {string} id - The documentation entry ID
   * @returns {string} Documentation URL or path
   */
  getDocumentationUrl(id) {
    const entry = this.getEntry(id);
    return entry?.documentationUrl || `/documentation/${id}`;
  }

  /**
   * Check if documentation exists for a given ID
   * @param {string} id - The documentation entry ID
   * @returns {boolean} True if documentation exists
   */
  hasDocumentation(id) {
    return this.documentationMap.has(id);
  }

  /**
   * Get all documentation entries (for admin/debug purposes)
   * @returns {Array} All documentation entries
   */
  getAllEntries() {
    return Array.from(this.documentationMap.values());
  }

  /**
   * Search documentation entries by title or content
   * @param {string} query - Search query
   * @returns {Array} Matching documentation entries
   */
  search(query) {
    if (!query || query.trim().length < 2) return [];
    
    const lowerQuery = query.toLowerCase().trim();
    const results = [];
    
    this.documentationMap.forEach(entry => {
      const titleMatch = entry.title.toLowerCase().includes(lowerQuery);
      const quickHelpMatch = entry.quickHelp.toLowerCase().includes(lowerQuery);
      const tagsMatch = entry.tags?.some(tag => tag.toLowerCase().includes(lowerQuery));
      
      if (titleMatch || quickHelpMatch || tagsMatch) {
        results.push({
          ...entry,
          relevanceScore: titleMatch ? 3 : quickHelpMatch ? 2 : 1
        });
      }
    });
    
    // Sort by relevance score
    return results.sort((a, b) => b.relevanceScore - a.relevanceScore);
  }

  /**
   * Register a new documentation entry dynamically
   * @param {Object} entry - Documentation entry object
   */
  registerEntry(entry) {
    if (!entry.id) {
      throw new Error('Documentation entry must have an ID');
    }
    
    this.documentationMap.set(entry.id, entry);
  }

  /**
   * Track documentation usage for analytics
   * @param {string} id - The documentation entry ID
   * @param {string} action - Action taken ('view', 'quickHelp', 'fullDoc')
   */
  trackUsage(id, action = 'view') {
    // For future analytics implementation
    console.debug(`Documentation usage: ${id} - ${action}`);
  }
}

// Create singleton instance
const documentationService = new DocumentationService();

export default documentationService; 